import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';

interface ExportColumn {
  header: string;
  key: string;
  width?: number;
  format?: (value: any) => any;
}



export interface ExportCustomerReportData {
  summary: {
    totalCustomers: number;
    newCustomersCount: number;
    activeCustomersCount: number;
    totalRevenue: number;
  };
  newCustomers: any[];
  topSpenders: any[];
  genderDistribution: any[];
  dailyStats: any[];
}

export interface ExportCourseReportData {
  summary: {
    totalRevenueLAK: number;
    totalRevenueTHB: number;
    totalCoursesSold: number;
    totalSessions: number;
  };
  soldCourses: any[];
  popularCourses: any[];
  revenueByType: any[];
  dailyStats: any[];
}

export interface ExportActiveCoursesReportData {
  summary: {
    totalActiveCourses: number;
    totalSessions: number;
    usedSessions: number;
    remainingSessions: number;
    usagePercentage: string;
  };
  activeCourses: any[];
  expiringCourses: any[];
}

export const exportToExcel = (
  data: any[],
  columns: ExportColumn[],
  filename: string
) => {
  // Transform data based on columns
  const transformedData = data.map(row => {
    const newRow: any = {};
    columns.forEach(col => {
      const value = getNestedValue(row, col.key);
      newRow[col.header] = col.format ? col.format(value) : value;
    });
    return newRow;
  });

  // Create worksheet
  const ws = XLSX.utils.json_to_sheet(transformedData);

  // Set column widths
  const wscols = columns.map(col => ({ wch: col.width || 15 }));
  ws['!cols'] = wscols;

  // Create workbook
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

  // Generate Excel file
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  // Save file
  saveAs(blob, `${filename}_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
};

// Helper function to get nested object value
const getNestedValue = (obj: any, path: string) => {
  return path.split('.').reduce((curr, key) => curr?.[key], obj);
};

// Export service charges to Excel
export const exportServiceChargesToExcel = (data: any[]) => {
  const columns: ExportColumn[] = [
    { header: 'ວັນທີ່', key: 'date', width: 12, format: (v) => dayjs(v).format('DD/MM/YYYY') },
    { header: 'ພະນັກງານ', key: 'userId.name', width: 20 },
    { header: 'ບໍລິການ', key: 'serviceId.name', width: 20 },
    { 
      header: 'ປະເພດ', 
      key: 'type', 
      width: 12,
      format: (v) => v === 'service' ? 'ບໍລິການ' : 'ແນະນຳ'
    },
    { 
      header: 'ລາຄາ (ກີບ)', 
      key: 'price', 
      width: 15,
      format: (v) => Number(v).toLocaleString()
    },
    { header: 'ໝາຍເຫດ', key: 'note', width: 30 },
    { header: 'ຜູ້ບັນທຶກ', key: 'createdBy.name', width: 20 },
    { 
      header: 'ເວລາບັນທຶກ', 
      key: 'createdAt', 
      width: 18,
      format: (v) => dayjs(v).format('DD/MM/YYYY HH:mm')
    },
  ];

  exportToExcel(data, columns, 'ServiceCharges');
};

// Export daily summary to Excel
export const exportDailySummaryToExcel = (data: any[], date: string) => {
  const columns: ExportColumn[] = [
    { header: 'ພະນັກງານ', key: 'user.name', width: 20 },
    { 
      header: 'ປະເພດ', 
      key: '_id.type', 
      width: 12,
      format: (v) => v === 'service' ? 'ບໍລິການ' : 'ແນະນຳ'
    },
    { header: 'ຈຳນວນ', key: 'count', width: 10 },
    { 
      header: 'ລາຍຮັບ (ກີບ)', 
      key: 'total', 
      width: 15,
      format: (v) => Number(v).toLocaleString()
    },
  ];

  exportToExcel(data, columns, `DailySummary_${date}`);
};

// Export monthly report to Excel
export const exportMonthlyReportToExcel = (data: any[], year: number, month: number) => {
  const columns: ExportColumn[] = [
    { header: 'ພະນັກງານ', key: 'user.name', width: 20 },
    { 
      header: 'ປະເພດ', 
      key: '_id.type', 
      width: 12,
      format: (v) => v === 'service' ? 'ບໍລິການ' : 'ແນະນຳ'
    },
    { header: 'ຈຳນວນ', key: 'count', width: 10 },
    { 
      header: 'ລາຍຮັບ (ກີບ)', 
      key: 'total', 
      width: 15,
      format: (v) => Number(v).toLocaleString()
    },
  ];

  exportToExcel(data, columns, `MonthlyReport_${year}_${month}`);
};





export const exportCustomerReportToExcel = (
  data: ExportCustomerReportData,
  dateRange: { startDate: string; endDate: string }
) => {
  const workbook = XLSX.utils.book_new();

  // Sheet 1: ສະຫຼຸບລາຍງານ
  const summaryData = [
    ['ລາຍງານລູກຄ້າ'],
    ['ຊ່ວງເວລາ:', `${dateRange.startDate} ຫາ ${dateRange.endDate}`],
    [],
    ['ສະຫຼຸບ'],
    ['ລູກຄ້າທັງໝົດ', data.summary.totalCustomers, 'ຄົນ'],
    ['ລູກຄ້າໃໝ່', data.summary.newCustomersCount, 'ຄົນ'],
    ['ລູກຄ້າ Active', data.summary.activeCustomersCount, 'ຄົນ'],
    ['ລາຍຮັບທັງໝົດ', new Intl.NumberFormat('lo-LA').format(data.summary.totalRevenue), '₭'],
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'ສະຫຼຸບ');

  // Sheet 2: ລູກຄ້າທີ່ໃຊ້ຈ່າຍສູງສຸດ
  if (data.topSpenders && data.topSpenders.length > 0) {
    const topSpendersData = data.topSpenders.map((item, index) => ({
      'ລຳດັບ': index + 1,
      'ລະຫັດລູກຄ້າ': item.customerId,
      'ຊື່': item.name,
      'ເບີໂທ': item.phone,
      'ຍອດໃຊ້ຈ່າຍ (₭)': new Intl.NumberFormat('lo-LA').format(item.totalSpent),
    }));
    const topSpendersSheet = XLSX.utils.json_to_sheet(topSpendersData);
    XLSX.utils.book_append_sheet(workbook, topSpendersSheet, 'ລູກຄ້າໃຊ້ຈ່າຍສູງ');
  }

  // Sheet 3: ລູກຄ້າໃໝ່
  if (data.newCustomers && data.newCustomers.length > 0) {
    const newCustomersData = data.newCustomers.map((item, index) => ({
      'ລຳດັບ': index + 1,
      'ລະຫັດລູກຄ້າ': item.customerId,
      'ຊື່': item.name,
      'ເບີໂທ': item.phone,
      'ອີເມວ': item.email || '-',
      'ວັນທີສະໝັກ': new Date(item.createdAt).toLocaleDateString('lo-LA'),
    }));
    const newCustomersSheet = XLSX.utils.json_to_sheet(newCustomersData);
    XLSX.utils.book_append_sheet(workbook, newCustomersSheet, 'ລູກຄ້າໃໝ່');
  }

  // Sheet 4: ສະຖິຕິຕາມເພດ
  if (data.genderDistribution && data.genderDistribution.length > 0) {
    const genderData = data.genderDistribution.map((item) => ({
      'ເພດ': item._id === 'male' ? 'ຊາຍ' : item._id === 'female' ? 'ຍິງ' : 'ອື່ນໆ',
      'ຈຳນວນ': item.count,
    }));
    const genderSheet = XLSX.utils.json_to_sheet(genderData);
    XLSX.utils.book_append_sheet(workbook, genderSheet, 'ສະຖິຕິຕາມເພດ');
  }

  // Sheet 5: ສະຖິຕິລາຍວັນ
  if (data.dailyStats && data.dailyStats.length > 0) {
    const dailyData = data.dailyStats.map((item) => ({
      'ວັນທີ': item._id,
      'ລູກຄ້າໃໝ່': item.newCustomers,
    }));
    const dailySheet = XLSX.utils.json_to_sheet(dailyData);
    XLSX.utils.book_append_sheet(workbook, dailySheet, 'ສະຖິຕິລາຍວັນ');
  }

  // Export
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
  saveAs(blob, `customer_report_${dateRange.startDate}_to_${dateRange.endDate}.xlsx`);
};

export const exportCourseReportToExcel = (
  data: ExportCourseReportData,
  dateRange: { startDate: string; endDate: string }
) => {
  const workbook = XLSX.utils.book_new();

  // Sheet 1: ສະຫຼຸບລາຍງານຄອສ
  const summaryData = [
    ['ລາຍງານຄອສ'],
    ['ຊ່ວງເວລາ:', `${dateRange.startDate} ຫາ ${dateRange.endDate}`],
    [],
    ['ສະຫຼຸບ'],
    ['ລາຍຮັບລວມກີບ', new Intl.NumberFormat('lo-LA').format(data.summary.totalRevenueLAK), '₭'],
    ['ລາຍຮັບລວມບາດ', new Intl.NumberFormat('lo-LA').format(data.summary.totalRevenueTHB), 'B'],
    ['ຄອສທີ່ຂາຍໄດ້', data.summary.totalCoursesSold, 'ຄັ້ງ'],
    ['Sessions ທັງໝົດ', data.summary.totalSessions, 'ຄັ້ງ'],
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'ສະຫຼຸບ');

  // Sheet 2: ຄອສຍອດນິຍົມ
  if (data.popularCourses && data.popularCourses.length > 0) {
    const popularData = data.popularCourses.map((item, index) => ({
      'ລຳດັບ': index + 1,
      'ຊື່ຄອສ': item.courseName,
      'ປະເພດ': item.courseType,
      'ຈຳນວນຂາຍ': item.totalQuantity,
      'Sessions': item.totalSessions,
      'ລາຍຮັບ (₭)': new Intl.NumberFormat('lo-LA').format(item.totalRevenue),
    }));
    const popularSheet = XLSX.utils.json_to_sheet(popularData);
    XLSX.utils.book_append_sheet(workbook, popularSheet, 'ຄອສຍອດນິຍົມ');
  }

  // Sheet 3: ທຸກຄອສທີ່ຂາຍ
  if (data.soldCourses && data.soldCourses.length > 0) {
    const soldData = data.soldCourses.map((item, index) => ({
      'ລຳດັບ': index + 1,
      'ຊື່ຄອສ': item.courseName,
      'ປະເພດ': item.courseType,
      'ຈຳນວນຂາຍ': item.totalQuantity,
      'Sessions': item.totalSessions,
      'ລາຍຮັບ (₭)': new Intl.NumberFormat('lo-LA').format(item.totalRevenue),
    }));
    const soldSheet = XLSX.utils.json_to_sheet(soldData);
    XLSX.utils.book_append_sheet(workbook, soldSheet, 'ຄອສທີ່ຂາຍ');
  }

  // Sheet 4: ລາຍຮັບຕາມປະເພດ
  if (data.revenueByType && data.revenueByType.length > 0) {
    const typeData = data.revenueByType.map((item) => ({
      'ປະເພດຄອສ': item._id,
      'ຈຳນວນ': item.count,
      'ລາຍຮັບ (₭)': new Intl.NumberFormat('lo-LA').format(item.totalRevenue),
    }));
    const typeSheet = XLSX.utils.json_to_sheet(typeData);
    XLSX.utils.book_append_sheet(workbook, typeSheet, 'ລາຍຮັບຕາມປະເພດ');
  }

  // Sheet 5: ສະຖິຕິລາຍວັນ
  if (data.dailyStats && data.dailyStats.length > 0) {
    const dailyData = data.dailyStats.map((item) => ({
      'ວັນທີ': item._id,
      'ຈຳນວນຂາຍ': item.coursesSold,
      'ລາຍຮັບ (₭)': new Intl.NumberFormat('lo-LA').format(item.revenue),
    }));
    const dailySheet = XLSX.utils.json_to_sheet(dailyData);
    XLSX.utils.book_append_sheet(workbook, dailySheet, 'ສະຖິຕິລາຍວັນ');
  }

  // Export
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
  saveAs(blob, `course_report_${dateRange.startDate}_to_${dateRange.endDate}.xlsx`);
};

export const exportActiveCoursesReportToExcel = (
  data: ExportActiveCoursesReportData,
  dateRange: { startDate: string; endDate: string }
) => {
  const workbook = XLSX.utils.book_new();

  // Sheet 1: ສະຫຼຸບ
  const summaryData = [
    ['ລາຍງານຄອສກຳລັງໃຊ້ງານ'],
    ['ຊ່ວງເວລາ:', `${dateRange.startDate} ຫາ ${dateRange.endDate}`],
    [],
    ['ສະຫຼຸບ'],
    ['ຄອສທີ່ໃຊ້ງານ', data.summary.totalActiveCourses, 'ຄອສ'],
    ['Sessions ລວມ', data.summary.totalSessions, 'ຄັ້ງ'],
    ['Sessions ໃຊ້ແລ້ວ', data.summary.usedSessions, 'ຄັ້ງ'],
    ['Sessions ເຫຼືອ', data.summary.remainingSessions, 'ຄັ້ງ'],
    ['ອັດຕາການໃຊ້', data.summary.usagePercentage, '%'],
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'ສະຫຼຸບ');

  // Sheet 2: ຄອສທີ່ກຳລັງໃຊ້ງານ
  if (data.activeCourses && data.activeCourses.length > 0) {
    const activeData = data.activeCourses.map((item, index) => ({
      'ລຳດັບ': index + 1,
      'ລູກຄ້າ': item.customer?.name,
      'ເບີໂທ': item.customer?.phone,
      'ຄອສ': item.course?.name,
      'ປະເພດ': item.course?.type,
      'Sessions ທັງໝົດ': item.totalSessions,
      'Sessions ໃຊ້ແລ້ວ': item.usedSessions,
      'Sessions ເຫຼືອ': item.remainingSessions,
      'ວັນໝົດອາຍຸ': new Date(item.expiryDate).toLocaleDateString('lo-LA'),
    }));
    const activeSheet = XLSX.utils.json_to_sheet(activeData);
    XLSX.utils.book_append_sheet(workbook, activeSheet, 'ຄອສກຳລັງໃຊ້ງານ');
  }

  // Sheet 3: ຄອສທີ່ໃກ້ໝົດອາຍຸ
  if (data.expiringCourses && data.expiringCourses.length > 0) {
    const expiringData = data.expiringCourses.map((item, index) => ({
      'ລຳດັບ': index + 1,
      'ລູກຄ້າ': item.customer?.name,
      'ເບີໂທ': item.customer?.phone,
      'ຄອສ': item.course?.name,
      'Sessions ເຫຼືອ': item.remainingSessions,
      'ວັນໝົດອາຍຸ': new Date(item.expiryDate).toLocaleDateString('lo-LA'),
    }));
    const expiringSheet = XLSX.utils.json_to_sheet(expiringData);
    XLSX.utils.book_append_sheet(workbook, expiringSheet, 'ຄອສໃກ້ໝົດອາຍຸ');
  }

  // Export
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
  saveAs(blob, `active_courses_report_${dateRange.startDate}_to_${dateRange.endDate}.xlsx`);
};



// Export customer to Excel
export const exportCustomerListReportToExcel = (data: any[], dateRange: { startDate: string; endDate: string }) => {
  const columns: ExportColumn[] = [
    { header: 'ລະຫັດລູກຄ້າ', key: 'customerId', width: 20 },
    { header: 'ຊື່ລູກຄ້າ', key: 'name', width: 20 },
    { header: 'ເບີໂທ', key: 'phone', width: 20 },
    { 
      header: 'ຍອດໃຊ້ຈ່າຍ (ກີບ)', 
      key: 'totalSpent', 
      width: 15,
      format: (v) => Number(v).toLocaleString()
    },
  ];

  exportToExcel(data, columns, `ລາຍຊື່ລູກຄ້າ${dateRange.startDate}_ຫາ_${dateRange.endDate}.xlsx`);
};

// Export service charges to Excel
export const exportCoursesPopulationReportToExcel = (data: any[], dateRange: { startDate: string; endDate: string }) => {
  const columns: ExportColumn[] = [
    { header: 'ຊື່ຄອສ', key: 'courseName', width: 20 },
    { header: 'ຈຳນວນຂາຍ', key: 'totalQuantity', width: 20 },
    { header: 'ຈຳນວນ Sessions', key: 'totalSessions', width: 20 },
    { 
      header: 'ລາຍຮັບ (ກີບ)', 
      key: 'totalRevenueLAK', 
      width: 15,
      format: (v) => Number(v).toLocaleString()
    },
     { 
      header: 'ລາຍຮັບ (ບາດ)', 
      key: 'totalRevenueTHB', 
      width: 15,
      format: (v) => Number(v).toLocaleString()
    },
  ];

  exportToExcel(data, columns, `ລາຍຊື່ຄອສທີ່ນິຍມສຸດ${dateRange.startDate}_ຫາ_${dateRange.endDate}.xlsx`);
};

// Export service charges to Excel
export const exportCoursesUseReportToExcel = (data: any[], dateRange: { startDate: string; endDate: string }) => {
  const columns: ExportColumn[] = [
    { header: 'ລູກຄ້າ', key: 'customer.name', width: 20 },
    { header: 'ຄອສ', key: 'course.name', width: 20 },
    { header: 'ຈຳນວນ Sessions', key: 'totalSessions', width: 20 },
    { header: 'ໃຊ້ແລ້ວ', key: 'usedSessions', width: 20 },
    { header: 'ຍັງເຫຼືອ', key: 'remainingSessions', width: 20 },
    { header: 'ວັນໝົດອາຍຸ', key: 'expiryDate', format: (v) => new Date(v).toLocaleDateString('lo-LA')} 
  ];

  exportToExcel(data, columns, `ຄອສກຳລັງໃຊ້ງານ${dateRange.startDate}_ຫາ_${dateRange.endDate}.xlsx`);
};




// Export saraly to Excel
export const exportSalariesToExcel = (data: any[], dateRange: { month: number; year: number }) => {
  const columns: ExportColumn[] = [
    { header: 'ພະນັກງານ', key: 'userId.name', width: 20 },
    { header: 'ມາຊ້າ', key: 'lateDays', width: 20 },
    { header: 'ມາຕົວຈິງ', key: 'actualWorkDays', width: 20 },
    { header: 'ຂາດ', key: 'absentDays', width: 20 },
    { header: 'ຊົ່ວໂມງ', key: 'totalWorkHours', width: 20 },
    { header: 'ເງິນຕາມຊົ່ວໂມງ', key: 'salaryByHours', width: 20 },
    { header: 'ໂບນັດບໍລິການ', key: 'serviceBonus', width: 20 },
    { header: 'ໂບນັດພິເສດ', key: 'specialBonus', width: 20 },
    { header: 'ເງິນຖືກຕັດ', key: 'deduction', width: 20 },
    { header: 'ເງິນລວມ', key: 'totalSalary', width: 20 },
    { header: 'ສະຖານະ', key: 'status', width: 20,format: (v) => v === 'draft' ? 'ຮ່າງ' : v === 'approved' ? 'ອະນຸມັດ'  : 'ຈ່າຍແລ້ວ' },
    { header: 'ໝາຍເຫດ', key: 'notes', width: 20 },
  ];

  exportToExcel(data, columns, `ສະຫຼຸບເງິນເດືອນ ${dateRange.month}/${dateRange.year}.xlsx`);
};



export const exportReciptToExcel = (data: any[]) => {
  const columns: ExportColumn[] = [
    { header: 'ເລກທີໃບບິນ', key: 'receiptNumber', width: 20 },
    { header: 'ລູກຄ້າ', key: 'customer.name', width: 20 },
    { header: 'ຍອດລວມ', key: 'total', width: 20 },
    { header: 'ສະກຸນເງິນ', key: 'currency', width: 20 },
    { header: 'ສະຖານະຊຳລະ', key: 'paymentStatus', width: 20,format: (v) => v === 'pending' ? 'ລໍຖ້າຊຳລະ' : v === 'partial' ? 'ຊຳລະແລ້ວ'  : v === 'paid' ? 'ຊຳລະບາງສ່ວນ'  : 'ຄືນເງິນ' },
    { header: 'ຈ່າຍແລ້ວ', key: 'amountPaid', width: 20 },
    { header: 'ຍັງຄ້າງຈ່າຍ', key: 'amountDue', width: 20 },
    { header: 'ວິທີຊຳລະ', key: 'method', width: 20 ,format: (v) => v === 'cash' ? 'ເງິນສົດ' : v === 'transfer' ? 'ເງິນໂອນ'  : 'ບັດສະມາຊິກ' },
    { header: 'ວັນທີອອກບິນ', key: 'createdAt', width: 20 ,format: (v) => new Date(v).toLocaleDateString('lo-LA')},
  
  ];

  exportToExcel(data, columns, `ລາຍການອອກບິນ.xlsx`);
};